import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  useTheme,
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import { useUserMenu } from '../../constants/menuConstants'
import {
  buildKiotHeaderNavButtonSx,
  muahangKiotLike,
} from './muahangKiotTokens'

const HOVER_CLOSE_MS = 280

const isPathActive = (pathname: string, startPoint: string, path?: string) => {
  if (startPoint && pathname.includes(startPoint)) return true
  if (path && (pathname === path || pathname.startsWith(path + '/'))) return true
  return false
}

const subMenuListItemSx = {
  mx: 0.75,
  my: 0,
  py: 0.5,
  px: 1,
  minHeight: 0,
  borderRadius: `${muahangKiotLike.kiotSubmenuItemRadiusPx}px`,
  color: muahangKiotLike.topNavText,
  fontFamily: muahangKiotLike.kiotFontStack,
  '& .MuiListItemIcon-root': {
    minWidth: 28,
    color: 'inherit',
  },
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.12)',
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(255,255,255,0.22)',
  },
  '&.Mui-selected:hover': {
    bgcolor: 'rgba(255,255,255,0.28)',
  },
} as const

const MuahangTopNavBar: React.FC = () => {
  const { pathname } = useLocation()
  const theme = useTheme()
  const menuItems = useUserMenu()
  const [openDropdown, setOpenDropdown] = useState<{ id: string; anchor: HTMLElement } | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleCloseDropdown = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setOpenDropdown(null)
      closeTimerRef.current = null
    }, HOVER_CLOSE_MS)
  }, [clearCloseTimer])

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  const closeDropdownNow = () => {
    clearCloseTimer()
    setOpenDropdown(null)
  }

  const navHighlightColors = useMemo(
    () => ({
      activeBg: theme.palette.muahang.navItemActiveBg,
      activeBgHover: theme.palette.muahang.navItemActiveBgHover,
    }),
    [theme.palette.muahang.navItemActiveBg, theme.palette.muahang.navItemActiveBgHover],
  )

  const navBtn = (active: boolean) => ({
    ...buildKiotHeaderNavButtonSx(active, navHighlightColors),
    alignSelf: 'stretch' as const,
    height: '100%',
    minHeight: 0,
    '& .MuiButton-startIcon': { mr: '10px', ml: 0 },
  })

  const openDropdownFromHover = (id: string, anchor: HTMLElement) => {
    clearCloseTimer()
    setOpenDropdown({ id, anchor })
  }

  const activeDropdown = openDropdown
    ? menuItems.find((m) => m.key === openDropdown.id && m.children.length > 0)
    : undefined

  const dropdownListSx = useMemo(
    () => ({
      py: 0.5,
      px: 0.25,
      minWidth: 240,
      maxHeight: 'min(70vh, 480px)' as const,
      overflow: 'auto' as const,
      bgcolor: theme.palette.muahang.kiotSubmenuPanelBg,
      color: muahangKiotLike.topNavText,
      borderRadius: `${muahangKiotLike.kiotSubmenuPaperRadiusPx}px`,
      boxShadow: '0 6px 16px rgba(0,0,0,0.22)',
    }),
    [theme.palette.muahang.kiotSubmenuPanelBg],
  )

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'stretch',
          overflowX: 'auto',
          overflowY: 'visible',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          gap: 0,
          pl: `${muahangKiotLike.kiotHeaderMainNavOffsetLeftPx}px`,
          py: `${muahangKiotLike.kiotHeaderRightItemMarginYPx}px`,
          boxSizing: 'border-box',
        }}
      >
        {menuItems.map((menuItem) => {
          const Icon = menuItem.icon
          const startIcon = Icon ? (
            <Icon style={{ fontSize: muahangKiotLike.kiotNavIconSizePx }} />
          ) : undefined

          if (!menuItem.children?.length) {
            const active = isPathActive(pathname, menuItem.startPoint, menuItem.path)
            return (
              <Button
                key={menuItem.key}
                component={Link}
                to={menuItem.path}
                color="inherit"
                size="small"
                disableElevation
                disableRipple
                startIcon={startIcon}
                sx={navBtn(active)}
              >
                {menuItem.label}
              </Button>
            )
          }

          const ddActive =
            pathname.includes(menuItem.startPoint) ||
            menuItem.children.some((sub) => pathname.includes(sub.startPoint))
          const menuOpen = openDropdown?.id === menuItem.key
          const looksActive = ddActive || menuOpen

          return (
            <Button
              key={menuItem.key}
              color="inherit"
              size="small"
              disableElevation
              disableRipple
              startIcon={startIcon}
              sx={navBtn(looksActive)}
              onMouseEnter={(e) => openDropdownFromHover(menuItem.key, e.currentTarget)}
              onMouseLeave={scheduleCloseDropdown}
            >
              {menuItem.label}
            </Button>
          )
        })}
      </Box>

      {activeDropdown && openDropdown ? (
        <Popper
          open
          anchorEl={openDropdown.anchor}
          placement="bottom-start"
          transition
          sx={{ zIndex: (z) => z.zIndex.modal + 2 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={120}>
              <Paper
                elevation={0}
                onMouseEnter={clearCloseTimer}
                onMouseLeave={scheduleCloseDropdown}
                sx={dropdownListSx}
              >
                <List
                  component="nav"
                  dense
                  disablePadding
                  sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}
                >
                  {activeDropdown!.children.map((sub) => {
                    const active = isPathActive(pathname, sub.startPoint, sub.path)
                    const SubIcon = sub.icon
                    return (
                      <ListItemButton
                        key={sub.key}
                        component={Link}
                        to={sub.path}
                        selected={active}
                        onClick={closeDropdownNow}
                        sx={subMenuListItemSx}
                      >
                        {SubIcon ? (
                          <ListItemIcon>
                            <SubIcon style={{ fontSize: 18 }} />
                          </ListItemIcon>
                        ) : null}
                        <ListItemText
                          primary={sub.label}
                          primaryTypographyProps={{
                            fontSize: `${muahangKiotLike.kiotNavFontSizePx}px`,
                            lineHeight: 1.25,
                            fontWeight: muahangKiotLike.kiotNavFontWeight,
                            color: 'inherit',
                            fontFamily: muahangKiotLike.kiotFontStack,
                          }}
                        />
                      </ListItemButton>
                    )
                  })}
                </List>
              </Paper>
            </Fade>
          )}
        </Popper>
      ) : null}
    </>
  )
}

export default MuahangTopNavBar
